/**
 * WASSER Admin - Component Overrides
 * B2B-стиль: профессиональные таблицы, чёткие кнопки
 */
import { alpha } from '@mui/material/styles';
import * as tokens from '@shared/theme/tokens.js';

/**
 * Get component overrides based on theme mode
 * @param {string} mode - 'light' | 'dark'
 * @param {object} palette - Theme palette
 * @returns {object} Component overrides
 */
export const getComponents = (mode, palette) => {
  const isDark = mode === 'dark';

  return {
    // ========================================
    // BUTTONS
    // ========================================
    MuiButton: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: 44px minimum touch target (WCAG 2.5.5)
          minHeight: 44,
          borderRadius: tokens.borderRadius.md,
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 12px ${alpha(palette.primary.main, 0.25)}`,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: alpha(palette.primary.main, 0.04),
          },
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.8125rem',
        },
        sizeMedium: {
          padding: '8px 20px',
        },
        sizeLarge: {
          padding: '12px 28px',
          fontSize: '1rem',
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: 44px minimum touch target (WCAG 2.5.5)
          minWidth: 44,
          minHeight: 44,
          borderRadius: tokens.borderRadius.md,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(palette.primary.main, 0.08),
          },
        },
        sizeSmall: {
          // MOBILE-FIRST: 40px для small (минимально приемлемо)
          minWidth: 40,
          minHeight: 40,
        },
        sizeLarge: {
          minWidth: 52,
          minHeight: 52,
        },
      },
    },

    // ========================================
    // CARDS & PAPER
    // ========================================
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: isDark
            ? '0 1px 3px rgba(0, 0, 0, 0.4)'
            : '0 1px 3px rgba(0, 0, 0, 0.08)',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: isDark
              ? '0 4px 12px rgba(0, 0, 0, 0.5)'
              : '0 4px 12px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // убираем MUI v6 gradient на Paper в dark mode
        },
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: isDark
            ? '0 1px 3px rgba(0, 0, 0, 0.4)'
            : '0 1px 3px rgba(0, 0, 0, 0.08)',
        },
        elevation2: {
          boxShadow: isDark
            ? '0 2px 6px rgba(0, 0, 0, 0.5)'
            : '0 2px 6px rgba(0, 0, 0, 0.1)',
        },
      },
    },

    // ========================================
    // INPUTS
    // ========================================
    MuiCheckbox: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: 44px minimum touch target
          padding: 10, // увеличиваем область клика
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: 44px minimum touch target
          padding: 10,
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: увеличенная область клика
          padding: 8,
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: tokens.borderRadius.md,
            transition: 'all 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: 44px minimum touch target
          minHeight: 44,
          borderRadius: tokens.borderRadius.md,
        },
        notchedOutline: {
          borderColor: isDark
            ? 'rgba(255, 255, 255, 0.15)'
            : 'rgba(0, 0, 0, 0.15)',
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: 44px minimum touch target
          minHeight: 44,
          borderRadius: tokens.borderRadius.md,
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.borderRadius.md,
          boxShadow: isDark
            ? '0 4px 20px rgba(0, 0, 0, 0.6)'
            : '0 4px 20px rgba(0, 0, 0, 0.15)',
        },
      },
    },

    // ========================================
    // DATA GRID (B2B-style)
    // ========================================
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          borderRadius: 12,
          '& .MuiDataGrid-main': {
            borderRadius: 12,
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: isDark
              ? alpha(palette.primary.main, 0.15)
              : alpha(palette.primary.main, 0.08),
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            fontSize: '0.8125rem',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            padding: '12px 16px',
          },
          '& .MuiDataGrid-row': {
            transition: 'background-color 0.15s ease',
            '&:hover': {
              backgroundColor: isDark
                ? alpha(palette.primary.main, 0.08)
                : alpha(palette.primary.main, 0.04),
            },
            '&.Mui-selected': {
              backgroundColor: isDark
                ? alpha(palette.primary.main, 0.2)
                : alpha(palette.primary.main, 0.1),
              '&:hover': {
                backgroundColor: isDark
                  ? alpha(palette.primary.main, 0.25)
                  : alpha(palette.primary.main, 0.15),
              },
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          },
        },
      },
    },

    // ========================================
    // TABLE (для обычных таблиц)
    // ========================================
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: '0.8125rem',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            backgroundColor: isDark
              ? alpha(palette.primary.main, 0.12)
              : alpha(palette.primary.main, 0.06),
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: isDark
              ? alpha(palette.primary.main, 0.06)
              : alpha(palette.primary.main, 0.03),
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          padding: '12px 16px',
        },
      },
    },

    // ========================================
    // CHIPS & BADGES
    // ========================================
    MuiChip: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: минимальная высота для кликабельных чипов
          minHeight: 32,
          borderRadius: 6,
          fontWeight: 500,
        },
        sizeSmall: {
          height: 24,
          fontSize: '0.75rem',
        },
        sizeMedium: {
          // MOBILE-FIRST: 36px для лучшего тач-таргета
          height: 36,
        },
        filled: {
          '&.MuiChip-colorSuccess': {
            backgroundColor: alpha(palette.success.main, 0.15),
            color: palette.success.main,
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: alpha(palette.warning.main, 0.15),
            color: isDark ? palette.warning.light : palette.warning.dark,
          },
          '&.MuiChip-colorError': {
            backgroundColor: alpha(palette.error.main, 0.15),
            color: palette.error.main,
          },
          '&.MuiChip-colorInfo': {
            backgroundColor: alpha(palette.info.main, 0.15),
            color: palette.info.main,
          },
        },
      },
    },

    // ========================================
    // DIALOGS & DRAWERS (MOBILE-FIRST)
    // ========================================
    MuiDialog: {
      styleOverrides: {
        paper: {
          // MOBILE-FIRST: базовые стили для мобильных
          margin: 8,
          width: 'calc(100% - 16px)',
          maxWidth: 'calc(100% - 16px)',
          maxHeight: 'calc(100% - 16px)',
          borderRadius: 12,
          boxShadow: isDark
            ? '0 8px 32px rgba(0, 0, 0, 0.6)'
            : '0 8px 32px rgba(0, 0, 0, 0.15)',
          // Desktop: возвращаем стандартные стили
          '@media (min-width: 600px)': {
            margin: 32,
            width: 'auto',
            maxWidth: 600,
            maxHeight: 'calc(100% - 64px)',
            borderRadius: 16,
          },
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: компактные стили для мобильных
          padding: '16px 16px 8px',
          fontSize: '1.1rem',
          fontWeight: 600,
          // Desktop: увеличенные отступы
          '@media (min-width: 600px)': {
            padding: '20px 24px 12px',
            fontSize: '1.25rem',
          },
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: компактные отступы
          padding: '8px 16px',
          // Desktop: стандартные отступы
          '@media (min-width: 600px)': {
            padding: '12px 24px',
          },
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: компактные стили + wrap для кнопок
          padding: '12px 16px 16px',
          gap: 8,
          flexWrap: 'wrap',
          // Desktop: стандартные отступы
          '@media (min-width: 600px)': {
            padding: '16px 24px 20px',
            flexWrap: 'nowrap',
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          backgroundImage: 'none',
          // MOBILE-FIRST: Drawer занимает 85% экрана
          width: '85vw',
          maxWidth: 320,
          // Desktop: стандартная ширина
          '@media (min-width: 600px)': {
            width: 'auto',
            maxWidth: 'none',
          },
        },
      },
    },

    // ========================================
    // ALERTS & SNACKBARS
    // ========================================
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: alpha(palette.success.main, 0.12),
          color: palette.success.main,
        },
        standardWarning: {
          backgroundColor: alpha(palette.warning.main, 0.12),
          color: isDark ? palette.warning.light : palette.warning.dark,
        },
        standardError: {
          backgroundColor: alpha(palette.error.main, 0.12),
          color: palette.error.main,
        },
        standardInfo: {
          backgroundColor: alpha(palette.info.main, 0.12),
          color: palette.info.main,
        },
      },
    },

    // ========================================
    // TABS & NAVIGATION
    // ========================================
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 44,
          padding: '12px 20px',
          '&.Mui-selected': {
            fontWeight: 600,
          },
        },
      },
    },

    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          marginLeft: 8,
          marginRight: 8,
        },
      },
    },

    // ========================================
    // TOOLTIPS & MENUS
    // ========================================
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: isDark ? '#1a1d24' : 'rgba(33, 33, 33, 0.95)',
          borderRadius: 6,
          fontSize: '0.75rem',
          padding: '6px 12px',
        },
        arrow: {
          color: isDark ? '#1a1d24' : 'rgba(33, 33, 33, 0.95)',
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: isDark
            ? '0 4px 20px rgba(0, 0, 0, 0.5)'
            : '0 4px 20px rgba(0, 0, 0, 0.12)',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: 44px minimum touch target
          minHeight: 44,
          borderRadius: 4,
          margin: '2px 6px',
          padding: '8px 12px',
          '&:hover': {
            backgroundColor: isDark
              ? alpha(palette.primary.main, 0.1)
              : alpha(palette.primary.main, 0.06),
          },
        },
      },
    },

    // ========================================
    // LIST ITEMS
    // ========================================
    MuiListItemButton: {
      styleOverrides: {
        root: {
          // MOBILE-FIRST: 44px minimum touch target
          minHeight: 44,
        },
      },
    },

    // ========================================
    // MISC
    // ========================================
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: isDark
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(0, 0, 0, 0.08)',
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        circle: {
          strokeLinecap: 'round',
        },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
        },
      },
    },
  };
};

export default getComponents;
